/**
 * ControlPanel API (`GET /me/permissions`) クライアント (権限管理 SBI-6)。
 *
 * - `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - ログインユーザー自身の所属グループと利用可能な機能 (和集合) を取得する。
 *   認証のみで呼べる API で、所属ゼロでも 200 (`features` は空配列)。
 * - この結果は **画面の出し分け (利便性) 専用**。API の認可は常にバックエンドが 403 で行う。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type { ApiErrorBody, MyPermissions } from './types';

export interface PermissionsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class PermissionsClient {
  private readonly options: PermissionsClientOptions;

  constructor(options: PermissionsClientOptions) {
    this.options = options;
  }

  /**
   * GET /me/permissions
   *
   * レスポンスの形が想定と違う場合 (`features` が配列でない等) は、権限を読み違えて
   * 画面を開きすぎないよう、例外にして呼び出し側で安全側 (何も出さない) に倒す。
   */
  async getMyPermissions(): Promise<MyPermissions> {
    const url = joinUrl(this.options.config.apiUrl, 'me/permissions');
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? '権限情報の取得に失敗しました';
      throw new ApiError(res.status, message, errorBody);
    }

    const body = (await res.json()) as Partial<MyPermissions> | null;
    if (!body || !Array.isArray(body.features)) {
      throw new Error('権限情報の形式が不正です');
    }
    return {
      user_sub: typeof body.user_sub === 'string' ? body.user_sub : '',
      permission_groups: Array.isArray(body.permission_groups) ? body.permission_groups : [],
      // 文字列以外は捨てる (想定外の値で権限を付与しない)
      features: body.features.filter((key): key is string => typeof key === 'string'),
    };
  }
}
